import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'

export default function MobileNav({ isOpen, onClose, user, profile, signOut }) {
  const navigate = useNavigate()

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  const handleNavigate = (path) => {
    onClose()
    navigate(path)
  }

  const handleSignOut = async () => {
    await signOut()
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className="fixed inset-y-0 left-0 w-80 max-w-[85vw] bg-white z-50 shadow-xl transform transition-transform safe-area"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-200 safe-top">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">B</span>
            </div>
            <span className="text-xl font-bold text-secondary-900">Beaver</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors touch-target"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100vh-73px)]">
          {user ? (
            <div className="p-4">
              {/* Profile Section */}
              <div className="mb-6 p-4 bg-secondary-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-lg">
                      {profile?.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-secondary-900 truncate">
                      {profile?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-secondary-600 truncate">{user.email}</p>
                    {profile?.role && (
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded ${
                        profile.role === 'developer'
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-secondary-200 text-secondary-700'
                      }`}>
                        {profile.role === 'developer' ? 'Developer' : 'Buyer'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Navigation Links */}
              <nav className="space-y-1">
                {/* Admin Link */}
                {profile?.admin_level && (
                  <>
                    <button
                      onClick={() => handleNavigate('/admin')}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-primary-700 font-medium hover:bg-primary-50 rounded-lg transition-colors touch-target"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span>Admin Dashboard</span>
                    </button>
                    <div className="h-px bg-secondary-200 my-2" />
                  </>
                )}

                {/* Buyer Navigation */}
                {profile?.role === 'buyer' && (
                  <button
                    onClick={() => handleNavigate('/dashboard/buyer')}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-secondary-900 hover:bg-secondary-50 rounded-lg transition-colors touch-target"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <span>My Shortlist</span>
                  </button>
                )}

                {/* Developer Navigation */}
                {profile?.role === 'developer' && (
                  <button
                    onClick={() => handleNavigate('/dashboard/developer')}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-secondary-900 hover:bg-secondary-50 rounded-lg transition-colors touch-target"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>My Projects</span>
                  </button>
                )}

                {/* Browse Projects */}
                <button
                  onClick={() => handleNavigate('/projects')}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-secondary-900 hover:bg-secondary-50 rounded-lg transition-colors touch-target"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Find</span>
                </button>

                {/* Settings */}
                <div className="h-px bg-secondary-200 my-2" />
                <button
                  onClick={() => handleNavigate('/settings')}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-secondary-900 hover:bg-secondary-50 rounded-lg transition-colors touch-target"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Settings</span>
                </button>

                {/* Sign Out */}
                <div className="h-px bg-secondary-200 my-2" />
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-error-600 hover:bg-error-50 rounded-lg transition-colors touch-target"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Sign Out</span>
                </button>
              </nav>
            </div>
          ) : (
            <div className="p-4">
              {/* Guest Navigation */}
              <nav className="space-y-1">
                <button
                  onClick={() => handleNavigate('/projects')}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-secondary-900 hover:bg-secondary-50 rounded-lg transition-colors touch-target"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Find</span>
                </button>

                <button
                  onClick={() => handleNavigate('/for-developers')}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-secondary-900 hover:bg-secondary-50 rounded-lg transition-colors touch-target"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>For Developers</span>
                </button>
              </nav>

              {/* Auth Actions */}
              <div className="mt-6 space-y-2">
                <button
                  onClick={() => handleNavigate('/signin')}
                  className="w-full btn-outline touch-target"
                >
                  Sign In
                </button>
                <button
                  onClick={() => handleNavigate('/signup')}
                  className="w-full btn-primary touch-target"
                >
                  Sign Up
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
